import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classes from './GarageContent.module.scss';
import AuthContext from '../../store/auth-context';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import DealerStockCard from '../DealerStockContent/DealerStockCard';
import { useDealerFavorites } from '../../store/dealer-favorites-context';
import InquiryThread, { attachmentsLabel } from './InquiryThread';
import VehicleStrip from './VehicleStrip';
import DealerWaitNote from './DealerWaitNote';
import PurchaseCard from './PurchaseCard';
import InspectionCard from './InspectionCard';
import BillingPanel from './BillingPanel';
import YourDetailsCard, { addressLines } from './YourDetailsCard';
import AuctionInspections from './AuctionInspections';
import AuctionPurchases from './AuctionPurchases';
import AuctionSavedCars from './AuctionSavedCars';
import useFocusedCard from './useFocusedCard';
import { getUser, updateUserData } from '../../services';
import {
    getDealerFavorites,
    getMyDealerInquiries,
    getMyDealerInquiry,
    replyToDealerInquiry,
    getMyDealerPurchases,
    setMyDealerPurchaseHandoff,
    getMyDealerInspections,
    decideDealerInspection,
    addDealerInspectionCheck,
    removeDealerInspectionCheck,
    getMyDealerInvoices,
    getMyDealerDeposit,
    carFromSnapshot,
    withTodaysPrices,
    INQUIRY_TYPE_LABELS,
    INQUIRY_STATUS_LABELS,
} from '../../services/dealerGarage';
import { formatUsd } from '../../services/dealerStock';

// A record the API hands back after an action carries its snapshot's price;
// keep the price withTodaysPrices gave the copy it replaces (and its other cars').
const keepCarPrice = (current, updated) => (current && current.vehicle && updated.vehicle
    ? { ...updated, vehicle: { ...updated.vehicle, usdPrice: current.vehicle.usdPrice } }
    : updated);
const keepTodaysPrice = (current, updated) => {
    const next = keepCarPrice(current, updated);
    if (!(updated.extraCars || []).length) return next;
    const before = new Map((current.extraCars || []).map((car) => [car.vehicleId, car]));
    return { ...next, extraCars: updated.extraCars.map((car) => keepCarPrice(before.get(car.vehicleId), car)) };
};

// The two sides of the garage, in the navbar's order. The auction entry points
// all name source=auction, and the links in dealer emails and notifications name
// a dealer-only tab or one dealer record. GarageContent works out the rest.
const SOURCES = [
    { id: 'auction', label: 'Auction', icon: 'fas fa-gavel' },
    { id: 'dealer', label: 'Dealer stock', icon: 'fas fa-car' },
];

// The side last looked at, so the navbar's plain /garage link reopens it. Not
// the 'garageSource' it used to be: that also holds the 'dealer' an old fallback
// stored for everyone who opened the garage, whichever side they actually use.
const SOURCE_KEY = 'garageSide';

// In the order a customer goes through them. Inquiries and invoices only
// exist for dealer stock.
const TABS = [
    { id: 'favorites', label: 'Saved Cars', icon: 'fas fa-heart', sources: ['auction', 'dealer'] },
    { id: 'inquiries', label: 'Inquiries', icon: 'fas fa-envelope', sources: ['dealer'] },
    { id: 'inspections', label: 'Inspections', icon: 'fas fa-clipboard-check', sources: ['auction', 'dealer'] },
    { id: 'purchases', label: 'Purchases', icon: 'fas fa-key', sources: ['auction', 'dealer'] },
    { id: 'billing', label: 'Billing', icon: 'fas fa-file-invoice-dollar', sources: ['dealer'] },
];

const HEADER_COPY = {
    auction: 'Auction cars you have saved, had inspected or bought.',
    dealer: 'Dealer-stock cars you have saved, asked about, had inspected or bought, plus your invoices. The same list follows you on importjdmmagic.com.',
};

// The shared labels are written for staff; a customer's fresh inquiry reads
// better as "Received" than "New".
const CUSTOMER_INQUIRY_STATUS_LABELS = {
    ...INQUIRY_STATUS_LABELS,
    new: 'Received',
};

// The query parameter that points at one record on each tab - the emails and
// in-app notifications link to /garage?tab=purchases&purchase=<id> and so on.
const FOCUS_PARAMS = {
    purchases: 'purchase',
    inspections: 'inspection',
    billing: 'invoice',
};

// A list response's records. Anything but 'Success' (the handler resolves
// error statuses too) is treated as a failed load, not an empty list.
const listFrom = (res) => {
    if (res?.message !== 'Success') throw new Error(res?.message || 'Request failed');
    return Array.isArray(res.data) ? res.data : [];
};

// Whether the member has anything on the dealer-stock side. A list that fails to
// load counts as empty: this only picks which side opens first.
const hasDealerRecords = async () => {
    const lists = await Promise.allSettled([
        getDealerFavorites(),
        getMyDealerInquiries(),
        getMyDealerInspections(),
        getMyDealerPurchases(),
    ]);
    return lists.some((list) => list.status === 'fulfilled' && list.value?.message === 'Success' && list.value.data?.length > 0);
};

const formatDate = (value) => {
    if (!value) return '';
    // A date-only string ("2026-09-08") parses as UTC midnight, which prints as
    // the previous day anywhere west of Greenwich; build it as a local date.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const d = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/** One conversation's card, brought into view when a link points at it. */
const InquiryCard = ({ focused, children }) => {
    const cardRef = useFocusedCard(focused);
    return (
        <article ref={cardRef} className={`${classes.card} ${focused ? classes.cardFocused : ''}`}>
            {children}
        </article>
    );
};

const GarageContent = () => {
    const authCtx = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const favoritesCtx = useDealerFavorites();

    // The side to open when neither the link nor this browser says which.
    const [fallbackSource, setFallbackSource] = useState(null);

    // Null while fallbackSource is still being worked out.
    const activeSource = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const asked = params.get('source');
        if (SOURCES.some((s) => s.id === asked)) return asked;
        // A link to a dealer-only tab, or to one dealer-stock record.
        const tab = TABS.find((t) => t.id === params.get('tab'));
        if ((tab && !tab.sources.includes('auction'))
            || ['inquiry', ...Object.values(FOCUS_PARAMS)].some((p) => params.has(p))) {
            return 'dealer';
        }
        let remembered = null;
        try { remembered = window.localStorage.getItem(SOURCE_KEY); } catch (err) { /* storage blocked */ }
        return SOURCES.some((s) => s.id === remembered) ? remembered : fallbackSource;
    }, [location.search, fallbackSource]);

    // Dealer stock for a member with records there, so they never open on an empty
    // side with their cars on the other. Otherwise auction, what this portal is for.
    const sourceUnknown = !activeSource;
    useEffect(() => {
        if (!sourceUnknown || !authCtx.isLoggedIn) return undefined;
        let active = true;
        hasDealerRecords().then((found) => {
            if (active) setFallbackSource(found ? 'dealer' : 'auction');
        });
        return () => { active = false; };
    }, [sourceUnknown, authCtx.isLoggedIn]);

    useEffect(() => {
        if (!activeSource) return;
        try { window.localStorage.setItem(SOURCE_KEY, activeSource); } catch (err) { /* storage blocked */ }
    }, [activeSource]);

    const tabs = useMemo(() => TABS.filter((t) => t.sources.includes(activeSource)), [activeSource]);

    const activeTab = useMemo(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        return tabs.some((t) => t.id === tab) ? tab : 'favorites';
    }, [location.search, tabs]);

    // The dealer-stock record a deep link points at on this tab; its card
    // scrolls into view and is highlighted.
    const focusId = useMemo(() => {
        const param = activeSource === 'dealer' ? FOCUS_PARAMS[activeTab] : null;
        return param ? new URLSearchParams(location.search).get(param) : null;
    }, [location.search, activeTab, activeSource]);

    const [favorites, setFavorites] = useState(null);
    const [inquiries, setInquiries] = useState(null);
    const [purchases, setPurchases] = useState(null);
    const [inspections, setInspections] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [deposit, setDeposit] = useState(null);
    // The member's own profile (GET /auth/me) for the Billing tab's "Your
    // details" card and to pre-fill a delivery address. undefined until it
    // is asked for, null when it could not be loaded.
    const [profile, setProfile] = useState(undefined);
    const [error, setError] = useState('');

    // The conversation a link points at (?inquiry=<id>).
    const linkedThreadId = useMemo(
        () => new URLSearchParams(location.search).get('inquiry'),
        [location.search]
    );

    // The open conversation. Seeded from the link so the "we replied" email
    // and in-app notification land directly on the right thread.
    const [openThreadId, setOpenThreadId] = useState(linkedThreadId);
    const [replySending, setReplySending] = useState(false);

    const replaceInquiry = (updated) => {
        if (!updated || !updated._id) return;
        setInquiries((prev) => (prev || []).map((i) => (i._id === updated._id ? keepTodaysPrice(i, updated) : i)));
    };

    // Opening a thread tells the server it was read (clears the unread badge)
    // and swaps in the freshest copy of the conversation.
    const openThread = useCallback((id) => {
        setOpenThreadId(id);
        getMyDealerInquiry(id)
            .then((res) => {
                if (res?.message === 'Success' && res.data) replaceInquiry(res.data);
            })
            .catch(() => {});
    }, []);

    // Resolves to true when sent; otherwise the reason, which the thread shows
    // under the reply box (a photo too big, say).
    const sendReply = async (id, body, files) => {
        setReplySending(true);
        try {
            const res = await replyToDealerInquiry(id, body, files);
            if (res?.message === 'Reply sent' && res.data) {
                replaceInquiry(res.data);
                return true;
            }
            return res?.message || 'Your reply could not be sent. Please try again.';
        } catch (err) {
            return 'Your reply could not be sent. Please try again.';
        } finally {
            setReplySending(false);
        }
    };

    // Never throws: the profile is a bonus everywhere it is used.
    const loadProfile = useCallback(async () => {
        try {
            const res = await getUser();
            setProfile(res?.status === 200 && res.data ? res.data : null);
        } catch (err) {
            setProfile(null);
        }
    }, []);

    const load = useCallback(async (tab) => {
        setError('');
        try {
            // The tabs that show a car's listing price get today's price
            // (withTodaysPrices) before they render, so a card never shows
            // the snapshot's figure and then changes.
            if (tab === 'favorites') {
                setFavorites(await withTodaysPrices(listFrom(await getDealerFavorites())));
            } else if (tab === 'inquiries') {
                setInquiries(await withTodaysPrices(listFrom(await getMyDealerInquiries())));
            } else if (tab === 'inspections') {
                setInspections(await withTodaysPrices(listFrom(await getMyDealerInspections())));
            } else if (tab === 'billing') {
                const [invoicesRes, depositRes] = await Promise.all([
                    getMyDealerInvoices(),
                    getMyDealerDeposit(),
                    loadProfile(),
                ]);
                setInvoices(listFrom(invoicesRes));
                // The deposit card is a bonus: if only it fails, still show the invoices.
                setDeposit(depositRes?.message === 'Success' && depositRes.data ? depositRes.data : null);
            } else {
                setPurchases(listFrom(await getMyDealerPurchases()));
            }
        } catch (err) {
            setError('Could not load your garage right now. Please try again in a moment.');
        }
    }, [loadProfile]);

    // Reloads on a new deep link too, so a notification about a record
    // created since the list loaded still finds it.
    // The auction side's sections load their own records.
    useEffect(() => {
        if (authCtx.isLoggedIn && activeSource === 'dealer') load(activeTab);
    }, [authCtx.isLoggedIn, activeSource, activeTab, focusId, load]);

    // A purchase still waiting on the customer's pickup / delivery choice
    // pre-fills a delivery address from their profile, so fetch it then.
    const choiceOpen = (purchases || []).some((p) => p.arrival && p.arrival.choiceOpen);
    const profileAsked = React.useRef(false);
    useEffect(() => {
        if (!authCtx.isLoggedIn || activeSource !== 'dealer' || activeTab !== 'purchases' || !choiceOpen) return;
        if (profileAsked.current || profile !== undefined) return;
        profileAsked.current = true;
        loadProfile();
    }, [authCtx.isLoggedIn, activeSource, activeTab, choiceOpen, profile, loadProfile]);

    const savedAddress = useMemo(
        () => (profile && profile.address ? addressLines(profile.address).join('\n') : ''),
        [profile]
    );

    // "Your details". Resolves to { ok, message }; a 409 is the phone number
    // already being on another account, which the server's message says.
    const saveProfile = async (payload) => {
        try {
            const res = await updateUserData(payload);
            if (res?.status === 200 && res.data) {
                setProfile((prev) => ({ ...(prev || {}), ...res.data }));
                return { ok: true };
            }
            return { ok: false, message: res?.message || 'Your details could not be saved. Please try again.' };
        } catch (err) {
            return { ok: false, message: 'Your details could not be saved. Please try again.' };
        }
    };

    const replacePurchase = (updated) => {
        if (!updated || !updated._id) return;
        setPurchases((prev) => (prev || []).map((p) => (p._id === updated._id ? updated : p)));
    };

    // Pickup or delivery, from a purchase card. Resolves to { ok, message,
    // conflict }. A 409 means the team has already arranged the hand-off:
    // reload so the card shows what was arranged, with the server's message.
    const setPurchaseHandoff = async (id, choice) => {
        const failed = 'Your choice could not be sent. Please try again.';
        try {
            const res = await setMyDealerPurchaseHandoff(id, choice);
            if (res?.data?._id) {
                replacePurchase(res.data);
                return { ok: true };
            }
            if (res?.code === 409) {
                await load('purchases');
                return { ok: false, conflict: true, message: res.message || failed };
            }
            return { ok: false, message: res?.message || failed };
        } catch (err) {
            return { ok: false, message: failed };
        }
    };

    const replaceInspection = (updated) => {
        if (!updated || !updated._id) return;
        setInspections((prev) => (prev || []).map((i) => (i._id === updated._id ? keepTodaysPrice(i, updated) : i)));
    };

    // The buy / pass answer on an inspection report. Resolves to { ok, message }
    // for the card to show. A 409 means it was already answered (another tab,
    // or the team took the answer by phone) or the report was withdrawn:
    // reload so the card shows where things actually stand.
    const decideInspection = async (id, decision, reason) => {
        try {
            const res = await decideDealerInspection(id, decision, reason);
            if (res?.message === 'Decision recorded' && res.data) {
                replaceInspection(res.data);
                return { ok: true };
            }
            if (res?.code === 409) await load('inspections');
            return { ok: false, message: res?.message || 'Your answer could not be sent. Please try again.' };
        } catch (err) {
            return { ok: false, message: 'Your answer could not be sent. Please try again.' };
        }
    };

    // The "what you'd like us to check" list. Both resolve to { ok, message }.
    // Edits close once the inspection is done: the API answers 409 to an add
    // and 404 to a remove then, so reload to show where it actually stands.
    const addInspectionCheck = async (id, text) => {
        try {
            const res = await addDealerInspectionCheck(id, text);
            if (res?.data?._id) {
                replaceInspection(res.data);
                return { ok: true };
            }
            if (res?.code === 409) await load('inspections');
            return { ok: false, message: res?.message || 'That could not be added. Please try again.' };
        } catch (err) {
            return { ok: false, message: 'That could not be added. Please try again.' };
        }
    };

    const removeInspectionCheck = async (id, checkId) => {
        try {
            const res = await removeDealerInspectionCheck(id, checkId);
            if (res?.data?._id) {
                replaceInspection(res.data);
                return { ok: true };
            }
            if (res?.code === 404 || res?.code === 409) await load('inspections');
            return { ok: false, message: res?.message || 'That could not be removed. Please try again.' };
        } catch (err) {
            return { ok: false, message: 'That could not be removed. Please try again.' };
        }
    };

    // Deep link (?inquiry=<id>): once the list has it, open the thread and
    // register the read with the server so the badge clears everywhere. The
    // link can come from inside the garage too (a report's "Ask a question"),
    // so this follows the id rather than only running on the first visit.
    const deepLinkHandled = React.useRef(null);
    useEffect(() => {
        if (!linkedThreadId) {
            deepLinkHandled.current = null;
            return;
        }
        if (deepLinkHandled.current === linkedThreadId || !(inquiries || []).some((i) => i._id === linkedThreadId)) return;
        deepLinkHandled.current = linkedThreadId;
        openThread(linkedThreadId);
    }, [inquiries, linkedThreadId, openThread]);

    // A heart un-toggled from this grid should drop the card, not leave a
    // stale one behind until the next visit.
    const favoriteIds = favoritesCtx.ids;
    const visibleFavorites = useMemo(
        () => (favorites || []).filter((f) => favoriteIds.has(f.vehicleId)),
        [favorites, favoriteIds]
    );

    const selectTab = (id) => navigate(`/garage?source=${activeSource}&tab=${id}`, { replace: true });

    // Stay on the same tab when the other side has it too.
    const selectSource = (id) => {
        const keep = TABS.find((t) => t.id === activeTab).sources.includes(id);
        navigate(`/garage?source=${id}&tab=${keep ? activeTab : 'favorites'}`, { replace: true });
    };

    // Until the first load lands. After a failed load the error banner says
    // what happened, so no spinner under it.
    const renderLoading = (text) => (error ? null : <div className={classes.state}><Loading text={text} /></div>);

    const renderFavorites = () => {
        if (favorites === null) return renderLoading('Saved Cars');
        if (visibleFavorites.length === 0) {
            return (
                <div className={classes.state}>
                    <EmptyContentCard
                        header="No saved cars yet"
                        message="Tap the heart on any dealer-stock car to keep it here. Saved cars show on the website and in this portal."
                    />
                    <Link to="/dealer-stock" className={classes.primaryLink}>Browse Dealer Stock</Link>
                </div>
            );
        }
        return (
            <div className={classes.grid}>
                {visibleFavorites.map((record) => (
                    <DealerStockCard key={record._id} car={carFromSnapshot(record)} />
                ))}
            </div>
        );
    };

    const renderInquiries = () => {
        if (inquiries === null) return renderLoading('Inquiries');
        if (inquiries.length === 0) {
            return (
                <div className={classes.state}>
                    <EmptyContentCard
                        header="No inquiries yet"
                        message="Questions, quote requests and inspection requests you send about dealer-stock cars are kept here so you can follow up."
                    />
                    <Link to="/dealer-stock" className={classes.primaryLink}>Browse Dealer Stock</Link>
                </div>
            );
        }
        return (
            <div className={classes.list}>
                {inquiries.map((inquiry) => (
                    <InquiryCard key={inquiry._id} focused={inquiry._id === linkedThreadId}>
                        <div className={classes.cardHead}>
                            <div>
                                <span className={classes.typeTag}>{INQUIRY_TYPE_LABELS[inquiry.type] || 'Inquiry'}</span>
                                <span className={classes.date}>{formatDate(inquiry.createdAt)}</span>
                                <span className={classes.origin}>via {inquiry.origin === 'portal' ? 'portal' : 'website'}</span>
                            </div>
                            <span className={`${classes.status} ${classes[`status_${inquiry.status}`]}`}>
                                {CUSTOMER_INQUIRY_STATUS_LABELS[inquiry.status] || inquiry.status}
                            </span>
                        </div>
                        <VehicleStrip record={inquiry} />
                        {inquiry.type === 'quote' && inquiry.quote && (
                            <dl className={classes.meta}>
                                <div><dt>Quote</dt><dd>{inquiry.quote.quoteType === 'imported' ? 'Imported' : 'Shipped'}</dd></div>
                                {inquiry.quote.port && <div><dt>Port</dt><dd>{inquiry.quote.port}</dd></div>}
                                <div><dt>Title</dt><dd>{inquiry.quote.includeTitle ? 'US title service' : 'No'}</dd></div>
                                {inquiry.quote.estimatedTotal ? (
                                    <div><dt>Estimate</dt><dd>{formatUsd(inquiry.quote.estimatedTotal)}</dd></div>
                                ) : null}
                            </dl>
                        )}
                        {/* Other cars they asked about while this conversation
                            was open joined it rather than starting another. */}
                        {(inquiry.extraCars || []).length > 0 && (
                            <>
                                <p className={classes.subhead}>Also asked about</p>
                                {inquiry.extraCars.map((car) => <VehicleStrip key={car._id || car.vehicleId} record={car} />)}
                            </>
                        )}
                        <DealerWaitNote record={inquiry} />
                        {openThreadId === inquiry._id ? (
                            <InquiryThread
                                inquiry={inquiry}
                                viewer="customer"
                                sending={replySending}
                                onSendReply={(body, files) => sendReply(inquiry._id, body, files)}
                            />
                        ) : (
                            (() => {
                                // Collapsed: preview the latest message so the card
                                // shows the state of the conversation, not just that
                                // one exists. The original submission counts as the
                                // opening message.
                                const all = [
                                    ...(inquiry.message ? [{ _id: 'original', author: 'customer', body: inquiry.message, createdAt: inquiry.createdAt }] : []),
                                    ...(inquiry.messages || []),
                                ];
                                const last = all[all.length - 1] || null;
                                return (
                                    <button
                                        type="button"
                                        className={classes.previewButton}
                                        onClick={() => openThread(inquiry._id)}
                                    >
                                        {last && (
                                            <span className={classes.previewRow}>
                                                <span className={`${classes.previewAvatar} ${last.author === 'staff' ? classes.previewAvatarStaff : ''}`}>
                                                    {last.author === 'staff' ? 'IJM' : 'You'}
                                                </span>
                                                <span className={classes.previewBody}>
                                                    <span className={classes.previewMeta}>
                                                        <strong>{last.author === 'staff' ? (last.authorName || 'Import JDM Magic') : 'You'}</strong>
                                                        <span className={classes.previewDate}>{formatDate(last.createdAt)}</span>
                                                        {inquiry.customerUnread > 0 && <span className={classes.unreadChip}>New</span>}
                                                    </span>
                                                    <span className={classes.previewText}>
                                                        {last.body
                                                            ? (last.body.length > 160 ? `${last.body.slice(0, 160).trimEnd()}…` : last.body)
                                                            : <><i className="fas fa-paperclip" aria-hidden="true"></i> Sent {attachmentsLabel(last.attachments)}</>}
                                                    </span>
                                                </span>
                                            </span>
                                        )}
                                        <span className={classes.previewCta}>
                                            <i className="fas fa-comments"></i>
                                            {(inquiry.messages || []).length > 0
                                                ? ` View conversation (${all.length} message${all.length === 1 ? '' : 's'})`
                                                : ' Reply / ask a follow-up'}
                                        </span>
                                    </button>
                                );
                            })()
                        )}
                    </InquiryCard>
                ))}
            </div>
        );
    };

    const renderInspections = () => {
        if (inspections === null) return renderLoading('Inspections');
        if (inspections.length === 0) {
            return (
                <div className={classes.state}>
                    <EmptyContentCard
                        header="No inspections yet"
                        message="Ask for an inspection on any dealer-stock car. The inspector's report, photos and video show up here, and you decide whether to buy."
                    />
                    <Link to="/dealer-stock" className={classes.primaryLink}>Browse Dealer Stock</Link>
                </div>
            );
        }
        return (
            <div className={classes.list}>
                {inspections.map((inspection) => (
                    <InspectionCard
                        key={inspection._id}
                        inspection={inspection}
                        focused={inspection._id === focusId}
                        onDecide={decideInspection}
                        onAddCheck={addInspectionCheck}
                        onRemoveCheck={removeInspectionCheck}
                    />
                ))}
            </div>
        );
    };

    const renderPurchases = () => {
        if (purchases === null) return renderLoading('Purchases');
        if (purchases.length === 0) {
            return (
                <div className={classes.state}>
                    <EmptyContentCard
                        header="No dealer-stock purchases yet"
                        message="When you buy a car from our dealer stock, its progress from order to hand-over is tracked here."
                    />
                    <Link to="/garage?source=auction&tab=purchases" className={classes.primaryLink}>Auction purchases</Link>
                </div>
            );
        }
        return (
            <div className={classes.list}>
                {purchases.map((purchase) => (
                    <PurchaseCard
                        key={purchase._id}
                        purchase={purchase}
                        focused={purchase._id === focusId}
                        savedAddress={savedAddress}
                        onHandoff={setPurchaseHandoff}
                    />
                ))}
            </div>
        );
    };

    const renderBilling = () => {
        if (invoices === null) return renderLoading('Billing');
        // Deposit details are only for a member who has (or had) one.
        const depositActivity = !!(deposit && (deposit.balance || (deposit.transactions || []).length));
        const details = <YourDetailsCard profile={profile} onSave={saveProfile} />;
        if (invoices.length === 0 && !depositActivity) {
            return (
                <>
                    <div className={classes.state}>
                        <EmptyContentCard
                            header="No invoices yet"
                            message="Invoices for inspections and dealer-stock cars show up here, along with the payments we've received."
                        />
                    </div>
                    {details}
                </>
            );
        }
        return (
            <BillingPanel
                invoices={invoices}
                deposit={depositActivity ? deposit : null}
                details={details}
                focusId={focusId}
            />
        );
    };

    // The auction side reuses the portal's own favourites view.
    const renderAuction = () => {
        if (activeTab === 'inspections') return <AuctionInspections />;
        if (activeTab === 'purchases') return <AuctionPurchases />;
        return <AuctionSavedCars />;
    };

    const renderDealer = () => (
        <>
            {error && <div className={classes.error}>{error}</div>}
            {activeTab === 'favorites' && renderFavorites()}
            {activeTab === 'inquiries' && renderInquiries()}
            {activeTab === 'inspections' && renderInspections()}
            {activeTab === 'purchases' && renderPurchases()}
            {activeTab === 'billing' && renderBilling()}
        </>
    );

    if (!activeSource) {
        return (
            <div className={classes.garage}>
                <div className={classes.header}>
                    <h1>My Garage</h1>
                </div>
                {renderLoading('My Garage')}
            </div>
        );
    }

    return (
        <div className={classes.garage}>
            <div className={classes.header}>
                <div>
                    <h1>My Garage</h1>
                    <p>{HEADER_COPY[activeSource]}</p>
                </div>
                {activeSource === 'auction' ? (
                    <Link to="/auction" className={classes.secondaryLink}>
                        <i className="fas fa-gavel" aria-hidden="true"></i> Browse Auctions
                    </Link>
                ) : (
                    <Link to="/dealer-stock" className={classes.secondaryLink}>
                        <i className="fas fa-car" aria-hidden="true"></i> Browse Dealer Stock
                    </Link>
                )}
            </div>

            <div className={classes.sourceSwitch} role="group" aria-label="Show cars from">
                {SOURCES.map((source) => (
                    <button
                        key={source.id}
                        type="button"
                        aria-pressed={activeSource === source.id}
                        className={`${classes.sourceOption} ${activeSource === source.id ? classes.sourceOptionActive : ''}`}
                        onClick={() => selectSource(source.id)}
                    >
                        <i className={source.icon} aria-hidden="true"></i>
                        {source.label}
                    </button>
                ))}
            </div>

            <div className={classes.tabs} role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
                        onClick={() => selectTab(tab.id)}
                    >
                        <i className={tab.icon} aria-hidden="true"></i>
                        {tab.label}
                        {activeSource === 'dealer' && tab.id === 'favorites' && favoritesCtx.ready && favoriteIds.size > 0 && (
                            <span className={classes.count}>{favoriteIds.size}</span>
                        )}
                    </button>
                ))}
            </div>

            {activeSource === 'auction' ? renderAuction() : renderDealer()}
        </div>
    );
};

export default GarageContent;
